import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import { useConnectedApps } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/api/getConnectedApps';
import { ADMIN_USER_ROLES, checkAccess } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import InboxItemSettings from '../InboxItemSettings';

const getAutomationEnabledContent = (isMasterAdmin: boolean, isAppEnabled: boolean) => {
  return {
    disabled: !isMasterAdmin || !isAppEnabled,
    content: !isAppEnabled ? (
      <React.Fragment>
        <Icon
          className={clsx('tw-text-[20px] tw-text-cfo-red-400', 'tw-mx-[5px]')}
          icon={ICONS.info}
          data-tooltip-id={'appsDisabledTooltip'}
        />
        <Tooltip id={'appsDisabledTooltip'} place="left" effect="solid">
          Unable to change this setting. No active integration setup.
        </Tooltip>
      </React.Fragment>
    ) : null,
  };
};

const getAutomationTooltipSettings = (isMasterAdmin: boolean) => {
  return !isMasterAdmin
    ? {
        id: 'automationTooltip',
        tooltipClasses: 'tw-max-w-[275px] !tw-w-full',
        place: 'bottom-start',
        effect: 'solid',
        content:
          'You are not allowed to change this setting. Please contact your system administrator.',
      }
    : undefined;
};

type AutomationSettingsProps = {
  isActive: number;
  layout: string;
  isEnabled: number;
  onSwitchChange: () => void;
};

const AutomationSettings = ({
  isActive,
  layout,
  isEnabled,
  onSwitchChange,
}: AutomationSettingsProps) => {
  const project = useSelector((store: RootState) => store.project);
  const loggedUser = useSelector((store: RootState) => store.loggedUser);

  // const { projectId } = useParams<{ projectId: string }>();
  const projectId = project.Selected.id;
  const connectedAppsQuery = useConnectedApps({
    projectId,
  });

  const isAppEnabled = useMemo(() => {
    return !!connectedAppsQuery.data?.quickbooks;
  }, [connectedAppsQuery.data]);

  const isMasterAdmin = checkAccess({ user: loggedUser.data, allowedRoles: ADMIN_USER_ROLES });

  const automationEnabled = getAutomationEnabledContent(isMasterAdmin, isAppEnabled);
  const automationTooltipSettings = getAutomationTooltipSettings(isMasterAdmin);

  return (
    <InboxItemSettings
      className={clsx('tw-mt-[20px]')}
      content="Enable automation?"
      isActive={isActive}
      layout={layout}
      isEnabled={isEnabled}
      disabled={automationEnabled}
      tooltipSettings={automationTooltipSettings}
      onSwitchChange={onSwitchChange}
    />
  );
};

export default AutomationSettings;
